import { Link, useNavigate } from "@tanstack/react-router";
import { MessageCircle, Repeat2, Heart, BarChart2, Share, MoreHorizontal, Bookmark, MapPin, AlertTriangle, Flag, Trash2, Link2 as LinkIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserAvatar } from "@/components/user-avatar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { AccountBadge, type AccountType } from "@/components/account-badge";
import { MediaGallery } from "@/components/media-gallery";
import { countryFlag } from "@/lib/country-flag";
import { optimizedImage } from "@/lib/image-url";
import { ReportDialog } from "@/components/report-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Category, PostType } from "@/lib/categories";

export interface PostMedia {
  id: string;
  url: string;
  media_type: "image" | "video" | "audio" | "document" | string;
  position: number;
  poster_url?: string | null;
  duration_seconds?: number | null;
  /** Video only: whether the clip has cleared moderation review. */
  review_state?: "pending" | "approved" | "rejected" | null;
}

export interface FeedPost {
  id: string;
  title: string;
  content: string;
  category: Category;
  post_type: PostType;
  status: string;
  image_url: string | null;
  location_name: string | null;
  upvotes: number;
  comment_count: number;
  view_count?: number;
  created_at: string;
  repost_of_id?: string | null;
  severity?: "info" | "warning" | "urgent" | "critical" | null;
  author: {
    id: string; username: string; full_name: string | null; avatar_url: string | null;
    account_type?: AccountType | null; verified?: boolean | null; is_founder?: boolean | null;
  } | null;
  community: { id: string; name: string; slug: string } | null;
  media?: PostMedia[] | null;
  support_count?: number;
  verify_count?: number;
  solution_count?: number;
  follower_count?: number;
  repost_count?: number;
  my_support?: boolean;
  my_verify?: boolean;
  my_follow?: boolean;
  my_save?: boolean;
  my_repost?: boolean;
}

const VIEWED_KEY = "cp:viewed";
function markViewedLocally(id: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(VIEWED_KEY);
    const set = new Set<string>(raw ? JSON.parse(raw) : []);
    if (set.has(id)) return false;
    set.add(id);
    sessionStorage.setItem(VIEWED_KEY, JSON.stringify(Array.from(set).slice(-500)));
    return true;
  } catch { return false; }
}

function formatCount(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return n > 0 ? String(n) : "";
}

export function PostCard({ post, onChanged }: { post: FeedPost; onChanged?: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEmergency = post.post_type === "emergency" || post.category === "emergency";

  const [like, setLike] = useState({ on: !!post.my_support, n: post.support_count ?? post.upvotes ?? 0 });
  const [reposted, setReposted] = useState({ on: !!post.my_repost, n: post.repost_count ?? 0 });
  const [saved, setSaved] = useState(!!post.my_save);
  const [views, setViews] = useState(post.view_count ?? 0);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [removed, setRemoved] = useState(false);
  const isOwn = !!user && post.author?.id === user.id;

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/posts/${post.id}`);
    toast.success("Link copied");
  };

  const deleteOwnPost = async () => {
    const { error } = await supabase.from("posts").delete().eq("id", post.id);
    if (error) return toast.error(error.message);
    setRemoved(true);
    toast.success("Post deleted");
    onChanged?.();
  };

  const mediaList: PostMedia[] = (post.media && post.media.length > 0)
    ? [...post.media].sort((a, b) => a.position - b.position)
    : post.image_url
      ? [{ id: post.id + "-legacy", url: post.image_url, media_type: "image", position: 0 }]
      : [];

  const cardRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          if (markViewedLocally(post.id)) {
            setViews((v) => v + 1);
            supabase.rpc("increment_post_view" as any, { _post_id: post.id }).then(() => {});
          }
          io.disconnect();
          return;
        }
      }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, [post.id]);

  const stop = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
  const requireAuth = () => {
    if (!user) { toast.error("Sign in to engage"); return false; }
    return true;
  };

  const toggleLike = async (e: React.MouseEvent) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    const next = !like.on;
    setLike({ on: next, n: like.n + (next ? 1 : -1) });
    if (next) {
      await supabase.from("votes").upsert(
        { user_id: user!.id, post_id: post.id, vote_type: "up" as any },
        { onConflict: "user_id,post_id" }
      );
    } else {
      await supabase.from("votes").delete().eq("user_id", user!.id).eq("post_id", post.id);
    }
    setBusy(false);
    onChanged?.();
  };

  const toggleSave = async (e: React.MouseEvent) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    if (next) {
      await supabase.from("saves").insert({ user_id: user!.id, post_id: post.id });
    } else {
      await supabase.from("saves").delete().eq("user_id", user!.id).eq("post_id", post.id);
    }
    setBusy(false);
  };

  const repost = async (e: React.MouseEvent) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    const next = !reposted.on;
    setReposted({ on: next, n: reposted.n + (next ? 1 : -1) });
    if (next) {
      const { error } = await supabase.from("posts").insert({
        author_id: user!.id,
        title: post.title,
        content: post.content,
        category: post.category as any,
        post_type: "update" as any,
        status: "open" as any,
        repost_of_id: post.id,
        image_url: post.image_url,
        location_name: post.location_name,
      });
      if (error) {
        setReposted({ on: false, n: reposted.n });
        toast.error("Repost failed");
      } else {
        toast.success("Reposted");
      }
    } else {
      const { error } = await supabase
        .from("posts")
        .delete()
        .eq("author_id", user!.id)
        .eq("repost_of_id", post.id);
      if (error) {
        setReposted({ on: true, n: reposted.n });
        toast.error("Couldn't undo repost");
      } else {
        toast.success("Repost removed");
      }
    }
    setBusy(false);
    onChanged?.();
  };

  const share = async (e: React.MouseEvent) => {
    stop(e);
    const url = `${window.location.origin}/posts/${post.id}`;
    try {
      if (navigator.share) await navigator.share({ title: post.content.slice(0, 80), url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch {}
  };

  const goToPost = () => navigate({ to: "/posts/$id", params: { id: post.id } });

  const displayName = post.author?.full_name?.trim() || post.author?.username || "User";
  const handle = post.author?.username ? `@${post.author.username}` : "";
  const flag = countryFlag(post.location_name);

  if (removed) return null;

  return (
    <article
      ref={cardRef as any}
      onClick={goToPost}
      className={cn(
        "flex cursor-pointer gap-3 border-b border-border px-4 py-3 transition-colors hover:bg-accent/30",
        isEmergency && "bg-emergency/[0.03]"
      )}
    >
      {/* Avatar */}
      {post.author && (
        <Link
          to="/profile/$username" params={{ username: post.author.username }}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0"
        >
          <UserAvatar
            name={post.author.full_name}
            username={post.author.username}
            avatarUrl={post.author.avatar_url}
            size={48}
            className="h-10 w-10"
          />
        </Link>
      )}

      {/* Body */}
      <div className="min-w-0 flex-1">
        {/* Header line */}
        <div className="flex items-center gap-1 text-[15px] leading-tight">
          {post.author && (
            <Link
              to="/profile/$username" params={{ username: post.author.username }}
              onClick={(e) => e.stopPropagation()}
              className="flex min-w-0 items-center gap-1 truncate"
            >
              <span className="truncate font-semibold text-foreground hover:underline">{displayName}</span>
              <AccountBadge accountType={post.author.account_type} verified={post.author.verified} isFounder={post.author.is_founder} />
              <span className="truncate text-muted-foreground">{handle}</span>
            </Link>
          )}
          <span className="text-muted-foreground">·</span>
          <span className="shrink-0 text-muted-foreground">
            {formatDistanceToNow(new Date(post.created_at), { addSuffix: false })}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={stop}
                className="ml-auto -mr-2 grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
                aria-label="More"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={stop} className="w-52">
              <DropdownMenuItem onClick={(e) => { stop(e as any); void copyLink(); }}>
                <LinkIcon className="mr-2 h-4 w-4" />Copy link
              </DropdownMenuItem>
              {isOwn ? (
                <DropdownMenuItem className="text-destructive focus:text-destructive"
                  onClick={(e) => { stop(e as any); void deleteOwnPost(); }}>
                  <Trash2 className="mr-2 h-4 w-4" />Delete post
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={(e) => { stop(e as any); if (!requireAuth()) return; setReportOpen(true); }}>
                  <Flag className="mr-2 h-4 w-4" />Report post
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="mt-0.5 whitespace-pre-wrap break-words text-[15px] leading-snug text-foreground">
          {isEmergency && <AlertTriangle className="mr-1 inline h-4 w-4 -translate-y-0.5 text-emergency" />}
          {post.content}
        </div>

        {/* Location chip */}
        {post.location_name && (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            {flag && <span aria-hidden>{flag}</span>}
            <MapPin className="h-3 w-3" />
            <span className="truncate">{post.location_name}</span>
          </div>
        )}

        {/* Media */}
        {mediaList.length > 0 && (
          <div className="mt-2 overflow-hidden rounded-2xl border border-border" onClick={stop}>
            <MediaGallery media={mediaList} ownerId={post.author?.id} />
          </div>
        )}

        {/* Action bar */}
        <div className="mt-2 -ml-2 flex max-w-md items-center justify-between text-muted-foreground">
          <ActionButton icon={MessageCircle} count={post.comment_count} label="Reply" onClick={goToPost} hoverColor="primary" />
          <ActionButton icon={Repeat2} count={reposted.n} label="Repost" onClick={repost} active={reposted.on} hoverColor="success" activeColor="success" />
          <ActionButton icon={Heart} count={like.n} label="Like" onClick={toggleLike} active={like.on} hoverColor="emergency" activeColor="emergency" filled={like.on} />
          <ActionButton icon={BarChart2} count={views} label="Views" onClick={(e) => { stop(e); goToPost(); }} hoverColor="primary" />
          <div className="flex items-center">
            <ActionButton icon={Bookmark} count={0} label="Save" onClick={toggleSave} active={saved} hoverColor="primary" activeColor="primary" filled={saved} />
            <ActionButton icon={Share} count={0} label="Share" onClick={share} hoverColor="primary" />
          </div>
        </div>
      </div>

      <div onClick={stop}>
        <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="post" targetId={post.id} />
      </div>
    </article>
  );
}

function ActionButton({
  icon: Icon, count, label, onClick, active, hoverColor, activeColor, filled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  hoverColor: "primary" | "success" | "emergency";
  activeColor?: "primary" | "success" | "emergency";
  filled?: boolean;
}) {
  const hover = {
    primary: "hover:bg-primary/10 hover:text-primary",
    success: "hover:bg-success/10 hover:text-success",
    emergency: "hover:bg-emergency/10 hover:text-emergency",
  }[hoverColor];
  const activeText = active && activeColor ? {
    primary: "text-primary",
    success: "text-success",
    emergency: "text-emergency",
  }[activeColor] : "";
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={cn("group flex items-center gap-1 rounded-full px-2 py-1.5 text-xs transition-colors", hover, activeText)}
    >
      <span className={cn("grid h-7 w-7 place-items-center rounded-full")}>
        <Icon className={cn("h-[18px] w-[18px]", filled && "fill-current")} />
      </span>
      <span className="tabular-nums">{formatCount(count)}</span>
    </button>
  );
}
